import { DatePicker } from '@kula-ai/components';
import type { DatePickerTriggerProps } from '@kula-ai/components';
import { DATE_PANEL_CLASS, toDate } from './dateRangeValue';

/** What a trigger is given: kula's own props. */
export type DateMonthTriggerProps = DatePickerTriggerProps;

export type DateMonthPickerProps = {
  value?: string | Date | null;
  /** Fires on the month click, which is also when kula closes the panel. */
  onChange: (date: Date) => void;
  /** Omit to use kula's own input trigger. */
  renderTrigger?: (props: DateMonthTriggerProps) => React.ReactNode;
  /** date-fns pattern for the trigger; month/year fields set their own. */
  dateFormat?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  triggerWidth?: string;
  minDate?: Date;
  maxDate?: Date;
  /** Bounds of the year grid, not of selection. */
  startYear?: number;
  endYear?: number;
  /** Pass a dialog's own node when a focus trap would swallow a body portal. */
  portalTarget?: HTMLElement | null;
  className?: string;
  calendarClassName?: string;
};

/**
 * The app's month/year picker, third sibling to `DateSinglePicker` and
 * `DateRangePicker`. It exists for the same reason they do: so every panel in
 * the app carries the marker class the outside-click whitelists look for, and
 * so callers stop re-deriving kula's defaults one field at a time.
 *
 * Unlike its siblings this does not impose the account date format — a month
 * field shows a month, and its callers pick the pattern.
 */
export const DateMonthPicker = ({
  value,
  onChange,
  renderTrigger,
  dateFormat = 'MM/yyyy',
  placeholder,
  disabled,
  error,
  triggerWidth,
  minDate,
  maxDate,
  startYear,
  endYear,
  portalTarget,
  className,
  calendarClassName,
}: DateMonthPickerProps) => (
  <DatePicker
    mode="month"
    value={toDate(value) ?? null}
    onChange={onChange}
    dateFormat={dateFormat}
    minDate={minDate}
    maxDate={maxDate}
    startYear={startYear}
    endYear={endYear}
    placeholder={placeholder}
    disabled={disabled}
    error={error}
    triggerWidth={triggerWidth}
    portalTarget={portalTarget}
    className={className}
    calendarClassName={[DATE_PANEL_CLASS, calendarClassName]
      .filter(Boolean)
      .join(' ')}
    // Omitting the prop entirely lets kula draw its own input trigger.
    {...(renderTrigger ? { renderTrigger } : {})}
  />
);
